import React, { createContext, useContext, useState, useCallback } from 'react';
import { DEMO_INGREDIENTS, DEMO_RECIPES, DEMO_BREAK_EVEN, DEMO_PNL } from '../utils/demoData';

const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

const AppContext = createContext();

// Unit conversion tables
const UNIT_CONVERSIONS = {
  lb: { oz: 16 },
  kg: { gram: 1000 },
  gallon: { oz: 128 },
  quart: { oz: 32 },
  liter: { ml: 1000 },
  each: { each: 1 },
  oz: { oz: 1 },
  gram: { gram: 1 },
  ml: { ml: 1 },
  case: { case: 1 },
};

export const convertUnit = (value, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return value;

  // Direct conversion
  if (UNIT_CONVERSIONS[fromUnit] && UNIT_CONVERSIONS[fromUnit][toUnit]) {
    return value * UNIT_CONVERSIONS[fromUnit][toUnit];
  }

  // Reverse conversion
  for (const [base, conversions] of Object.entries(UNIT_CONVERSIONS)) {
    if (conversions[fromUnit] && base === toUnit) {
      return value / conversions[fromUnit];
    }
    if (base === fromUnit && conversions[toUnit]) {
      return value * conversions[toUnit];
    }
  }

  // Weight cross-conversions
  const toOz = { lb: 16, oz: 1, gram: 0.035274, kg: 35.274 };
  const toGram = { gram: 1, kg: 1000, oz: 28.3495, lb: 453.592 };
  const toMl = { ml: 1, liter: 1000, oz: 29.5735, quart: 946.353, gallon: 3785.41 };

  if (toOz[fromUnit] && toOz[toUnit]) {
    return (value * toOz[fromUnit]) / toOz[toUnit];
  }
  if (toGram[fromUnit] && toGram[toUnit]) {
    return (value * toGram[fromUnit]) / toGram[toUnit];
  }
  if (toMl[fromUnit] && toMl[toUnit]) {
    return (value * toMl[fromUnit]) / toMl[toUnit];
  }

  return value; // fallback
};

export const UNITS = ['lb', 'oz', 'gram', 'kg', 'gallon', 'quart', 'liter', 'ml', 'each', 'case'];

export const CATEGORIES = [
  'Dairy', 'Meat', 'Produce', 'Dry Goods', 'Sauces', 'Spices',
  'Packaging', 'Beverages', 'Frozen', 'Other'
];

export function AppProvider({ children }) {
  // Ingredients database
  const [ingredients, setIngredients] = useState([]);

  // Recipes
  const [recipes, setRecipes] = useState([]);

  // Labor settings
  const [laborSettings, setLaborSettings] = useState({
    defaultWage: 15,
    laborBurdenPercent: 25,
  });

  // Break-even settings
  const [breakEvenData, setBreakEvenData] = useState({
    rent: 0,
    utilities: 0,
    insurance: 0,
    software: 0,
    payroll: 0,
    managerSalary: 0,
    loanPayments: 0,
    otherFixed: 0,
    avgOrderValue: 25,
    avgContributionMargin: 65,
  });

  // Specials
  const [specials, setSpecials] = useState([]);

  // P&L data
  const [pnlData, setPnlData] = useState({
    revenue: 0,
    cogs: 0,
    labor: 0,
    rent: 0,
    utilities: 0,
    marketing: 0,
    software: 0,
    insurance: 0,
  });

  // Ingredient CRUD
  const addIngredient = useCallback((ingredient) => {
    const usableQty = ingredient.purchaseQuantity * (ingredient.yieldPercent / 100);
    const costPerUsableUnit = usableQty > 0 ? ingredient.purchaseCost / usableQty : 0;

    setIngredients(prev => [...prev, {
      ...ingredient,
      id: generateId(),
      usableQuantity: usableQty,
      costPerUsableUnit,
      wastePercent: 100 - ingredient.yieldPercent,
      priceHistory: [{ date: new Date().toISOString().slice(0, 10), cost: Number(ingredient.purchaseCost) || 0 }],
    }]);
  }, []);

  const updateIngredient = useCallback((id, updates) => {
    setIngredients(prev => prev.map(ing => {
      if (ing.id !== id) return ing;
      const updated = { ...ing, ...updates };
      const usableQty = updated.purchaseQuantity * (updated.yieldPercent / 100);
      updated.usableQuantity = usableQty;
      updated.costPerUsableUnit = usableQty > 0 ? updated.purchaseCost / usableQty : 0;
      updated.wastePercent = 100 - updated.yieldPercent;
      // Track price change
      const newCost = Number(updated.purchaseCost) || 0;
      const oldCost = Number(ing.purchaseCost) || 0;
      if (newCost !== oldCost && newCost > 0) {
        const history = [...(updated.priceHistory || [])];
        const today = new Date().toISOString().slice(0, 10);
        if (history.length === 0 || history[history.length - 1].date !== today) {
          history.push({ date: today, cost: newCost });
        } else {
          history[history.length - 1].cost = newCost;
        }
        updated.priceHistory = history;
      }
      return updated;
    }));
  }, []);

  const deleteIngredient = useCallback((id) => {
    setIngredients(prev => prev.filter(ing => ing.id !== id));
  }, []);

  const bulkAddIngredients = useCallback((newIngredients) => {
    const processed = newIngredients.map(ing => {
      const usableQty = ing.purchaseQuantity * (ing.yieldPercent / 100);
      return {
        ...ing,
        id: generateId(),
        usableQuantity: usableQty,
        costPerUsableUnit: usableQty > 0 ? ing.purchaseCost / usableQty : 0,
        wastePercent: 100 - ing.yieldPercent,
      };
    });
    setIngredients(prev => [...prev, ...processed]);
  }, []);

  // Recipe CRUD
  const addRecipe = useCallback((recipe) => {
    setRecipes(prev => [...prev, { ...recipe, id: generateId() }]);
  }, []);

  const updateRecipe = useCallback((id, updates) => {
    setRecipes(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
  }, []);

  const deleteRecipe = useCallback((id) => {
    setRecipes(prev => prev.filter(r => r.id !== id));
  }, []);

  // Specials CRUD
  const addSpecial = useCallback((special) => {
    setSpecials(prev => [...prev, { ...special, id: generateId() }]);
  }, []);

  const deleteSpecial = useCallback((id) => {
    setSpecials(prev => prev.filter(s => s.id !== id));
  }, []);

  // Calculate recipe costs
  const calculateRecipeCost = useCallback((recipeIngredients) => {
    let totalIngredientCost = 0;

    for (const ri of recipeIngredients) {
      const ingredient = ingredients.find(i => i.id === ri.ingredientId);
      if (!ingredient) continue;

      const convertedAmount = convertUnit(ri.amount, ri.unit, ingredient.purchaseUnit);
      totalIngredientCost += convertedAmount * ingredient.costPerUsableUnit;
    }

    return totalIngredientCost;
  }, [ingredients]);

  // Calculate yield (how many can we make)
  const calculateYield = useCallback((recipeIngredients) => {
    let minUnits = Infinity;
    let bottleneck = '';

    for (const ri of recipeIngredients) {
      const ingredient = ingredients.find(i => i.id === ri.ingredientId);
      if (!ingredient || ri.amount === 0) continue;

      const availableInRecipeUnit = convertUnit(
        ingredient.usableQuantity,
        ingredient.purchaseUnit,
        ri.unit
      );
      const possibleUnits = Math.floor(availableInRecipeUnit / ri.amount);

      if (possibleUnits < minUnits) {
        minUnits = possibleUnits;
        bottleneck = ingredient.name;
      }
    }

    return {
      maxUnits: minUnits === Infinity ? 0 : minUnits,
      bottleneck,
    };
  }, [ingredients]);

  // Load demo data
  const loadDemoData = useCallback(() => {
    // Add ingredients and collect their generated IDs
    const newIngredients = DEMO_INGREDIENTS.map(ing => {
      const usableQty = ing.purchaseQuantity * (ing.yieldPercent / 100);
      return {
        ...ing,
        id: generateId(),
        usableQuantity: usableQty,
        costPerUsableUnit: usableQty > 0 ? ing.purchaseCost / usableQty : 0,
        wastePercent: 100 - ing.yieldPercent,
      };
    });
    setIngredients(newIngredients);

    // Build recipes with resolved ingredient IDs
    const newRecipes = DEMO_RECIPES.map(dr => {
      const recipeIngredients = (dr.ingredientRefs || []).map(ref => ({
        ingredientId: newIngredients[ref.ingredientIndex]?.id || '',
        amount: ref.amount,
        unit: ref.unit,
      }));

      // Calculate costs
      let ingredientCost = 0;
      for (const ri of recipeIngredients) {
        const ing = newIngredients.find(i => i.id === ri.ingredientId);
        if (!ing) continue;
        const converted = convertUnit(Number(ri.amount) || 0, ri.unit, ing.purchaseUnit);
        ingredientCost += converted * ing.costPerUsableUnit;
      }
      const packagingCost = Number(dr.packagingCost) || 0;
      const totalMinutes = (Number(dr.prepMinutes) || 0) + (Number(dr.cookMinutes) || 0);
      const loadedRate = (Number(dr.laborWage) || 0) * (1 + (Number(dr.laborBurdenPercent) || 0) / 100);
      const laborCost = loadedRate * totalMinutes / 60;
      const totalCost = ingredientCost + packagingCost + laborCost;
      const menuPrice = Number(dr.menuPrice) || 0;
      const grossProfit = menuPrice - totalCost;
      const grossMargin = menuPrice > 0 ? (grossProfit / menuPrice) * 100 : 0;
      const foodCostPercent = menuPrice > 0 ? (ingredientCost / menuPrice) * 100 : 0;
      const primeCostPercent = menuPrice > 0 ? ((ingredientCost + laborCost) / menuPrice) * 100 : 0;

      return {
        id: generateId(),
        name: dr.name,
        category: dr.category,
        menuPrice: dr.menuPrice,
        targetFoodCostPercent: dr.targetFoodCostPercent,
        targetPrimeCostPercent: dr.targetPrimeCostPercent,
        packagingCost: dr.packagingCost,
        prepMinutes: dr.prepMinutes,
        cookMinutes: dr.cookMinutes,
        laborWage: dr.laborWage,
        laborBurdenPercent: dr.laborBurdenPercent,
        notes: dr.notes,
        recipeIngredients,
        ingredientCost, totalCost, grossProfit, grossMargin,
        foodCostPercent, primeCostPercent, laborCost,
      };
    });
    setRecipes(newRecipes);

    setBreakEvenData(DEMO_BREAK_EVEN);
    setPnlData(DEMO_PNL);
  }, []);

  const value = {
    ingredients, setIngredients,
    addIngredient, updateIngredient, deleteIngredient, bulkAddIngredients,
    recipes, addRecipe, updateRecipe, deleteRecipe,
    laborSettings, setLaborSettings,
    breakEvenData, setBreakEvenData,
    specials, addSpecial, deleteSpecial,
    pnlData, setPnlData,
    calculateRecipeCost, calculateYield,
    convertUnit, loadDemoData,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};
